import * as KendoChart from '@progress/kendo-react-charts'
import { DateRangePicker } from '@progress/kendo-react-dateinputs'
import { ExcelExport } from '@progress/kendo-react-excel-export'
import { Grid, GridCell, GridColumn } from '@progress/kendo-react-grid'
import bodybuilder from 'bodybuilder'
import { LoadSegmentCodes } from 'components/common/load-partner-codes/component'
import { DropdownComponent } from 'components/dropdown/component'
import { HeaderComponent } from 'components/header/component'
import { format } from 'date-fns'
import { AggregateIntervalOptions, appendTimezoneIfAvailable, DateRangesAsDropdownOptions, getMessageSendDateFilterField, SuspectFilterDropdownOptions
} from 'elasticsearch/constants'
import { AggregationType, DateRanges, FilterType, IndexName, toFilter } from 'elasticsearch/constants'
import { ResponseAggregate, ResponsePayload } from 'elasticsearch/types'
import {
  ElasticsearchComponent,
  ElasticsearchProps,
} from 'generic/elasticSearchComponent'
import * as GenericRedux from 'generic/genericRedux'
import * as React from 'react'
import { Button } from 'reactstrap'
import { DEFAULT_COMMUNITY_PARTNER_CODES } from 'shared_server_client/constants'
import * as Constants from './constants'
import { RasaAnalyticsComponent } from './rasa-analytics-component'
import './styles.css'
import { ConnectedComponentClass } from "react-redux"
import {Fields} from "../../shared/modals";
import {ComponentType} from "react";
import * as Utils from './utils'
import { utcToZonedTime } from 'date-fns-tz'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { PDFReportHeader } from './PDFReportHeader'
import { Loading } from 'components/loading'

const initialState = {
  selectedDateRange: DateRangesAsDropdownOptions[4],
  selectedInterval: AggregateIntervalOptions[0],
  selectedSegmentCode: DEFAULT_COMMUNITY_PARTNER_CODES.ALL,
}
export class OpensClicksComponent extends RasaAnalyticsComponent<any, any> {
  constructor(props) {
    super(props, initialState)
  }

  public componentDidMount() {
    super.componentDidMount()
    this.minCustomDateRange()
  }

  public render() {
    return(
      <div className="analytics-component">
        <HeaderComponent
          title={'ANALYTICS'}
          subTitle={'Opens & Clicks'}
          description={[
            'Opens - The number of times your newsletter has been opened by your subscribers.',
            'Clicks - The number of times any of your subscribers have clicked on a link in your newsletter.',
            'Click to Open - the number of clicks out of the number of opens',
          ]}
        />
        <div className="dropdown-header">
          <div className="dropdown-inline">
            <DropdownComponent data={DateRangesAsDropdownOptions}
                                selected={this.state.selectedDateRange.key}
                                onChange={this.dateChanged}/>
          </div>
          <div className="dropdown-inline">
            <DropdownComponent data={AggregateIntervalOptions}
                                selected={this.state.selectedInterval.key}
                                onChange={this.intervalChanged}/>
          </div>
          <div className="dropdown-inline">
            <DropdownComponent data={SuspectFilterDropdownOptions}
                               selected={this.state.selectedSuspectClick.key}
                               onChange={this.suspectedClickChanged}/>
          </div>
        </div>
        <div className="dropdown-segment-code">
        <LoadSegmentCodes segmentCode={this.state.selectedSegmentCode} setSegmentCode={this.segmentCodeChanged}
          hideLabel={true}></LoadSegmentCodes>
        </div>
        <div className="date-range-picker">
        {this.state.selectedDateRange.key === '7' ?
        <DateRangePicker min={this.state.minCustomDateRange}
                          max ={new Date()}
                          onChange={this.createCustomDate} /> : null}
        </div>
        {this.state.isFilterLoaded &&
        <AnalyticsOpensClicksComponent
          dateRange={this.state.selectedDateRange.value}
          dateRangeLabel={this.state.selectedDateRange.description}
          timezone={this.state.timezone}
          useTimezoneForAnalytics={this.state.useTimezoneForAnalytics}
          selectedSegmentCode={this.state.selectedSegmentCode}
          selectedInterval={this.state.selectedInterval.value}
          suspectClick={this.state.selectedSuspectClick.value}
          timestampLabel={this.timestampLabel}
        />}
      </div>
    )
  }
}

// const totalOpenColor = '#7fae1b'
const uniqueOpenColor = '#004f6d'
// const totalClickColor = '#8ae0e3'
const uniqueClickColor = '#4ecdc4'
const uniqueClickToOpenColor = '#ff6b6b'

interface Point {
  timestamp: string,
  totalClick: number,
  totalClickRate: number,
  totalDelivered: number,
  totalOpen: number,
  totalOpenRate: number,
  uniqueClick: number,
  uniqueClickRate: number,
  uniqueDelivered: number,
  uniqueOpen: number,
  uniqueOpenRate: number,
  uniqueClickToOpenRate?: number,
  suspectClick?: number,
}

type Points = Point[]

interface EngagementProps extends ElasticsearchProps<Points> {
  dateRange: string,
  selectedInterval: string,
  hideGrid: boolean,
  hideMetricSelector?: boolean,
  selectedSegmentCode: string,
  suspectClick?: any
  timezone?: string,
  useTimezoneForAnalytics?: any,
  timestampLabel?: (dateNumber: string | number, dateString: string) => string
  dateRangeLabel?: string
}

interface MetricConfig {
  key: string,
  label: string,
  field: string,
  color: string,
  axis: string,
  selected: boolean,
}

interface EngagementState {
  loaded: boolean,
  displayLabel: boolean,
  availableMetrics: MetricConfig[],
  selectedMetrics: MetricConfig[],
  isLoadingData: boolean,
}

const EVENT_AGGREGATION: string = 'event'
const DATE_AGGREGATION: string = 'date'

class OpensClicksClass extends ElasticsearchComponent<Points, EngagementProps, EngagementState> {
  private xlsxExport: any = null
  private pdfExportRef: React.RefObject<HTMLDivElement> = React.createRef()
  constructor(p: EngagementProps) {
    super(p, IndexName.EVENTS)
    const availableMetrics: MetricConfig[] = [
      { key: 'uniqueClick', label: 'Unique Click', field: 'uniqueClickRate', color: uniqueClickColor, axis: 'left' as const, selected: true },
      { key: 'clickToOpen', label: 'Click To Open', field: 'uniqueClickToOpenRate', color: uniqueClickToOpenColor, axis: 'left' as const, selected: true },
      { key: 'uniqueOpen', label: 'Unique Open', field: 'uniqueOpenRate', color: uniqueOpenColor, axis: 'left' as const, selected: true },
      { key: 'totalClick', label: 'Total Click', field: 'totalClickRate', color: '#ff9999', axis: 'left' as const, selected: false },
      { key: 'totalOpen', label: 'Total Open', field: 'totalOpenRate', color: '#99ccff', axis: 'left' as const, selected: false },
      { key: 'delivered', label: 'Delivered', field: 'uniqueDelivered', color: '#90EE90', axis: 'right' as const, selected: false },
      { key: 'hardBounce', label: 'Hard Bounce', field: 'uniqueBounces', color: '#FF6347', axis: 'right' as const, selected: false },
      { key: 'softBounce', label: 'Soft Bounce', field: 'uniqueSoftBounces', color: '#FFA500', axis: 'right' as const, selected: false },
    ]
    this.state = {
      loaded: false,
      displayLabel: true,
      availableMetrics,
      selectedMetrics: availableMetrics.filter(m => m.selected),
      isLoadingData: false,
    }
    this.reportName = Constants.REPORT_NAMES.OPEN_CLICKS
  }

  public parseResponse(payload: ResponsePayload): Promise<Points> {
    this.setState({
      loaded: true,
      isLoadingData: false,
    })
    const dateAggregationsBuckets = payload.aggregations[DATE_AGGREGATION].buckets
    .filter((aggregation: ResponseAggregate) => {
      return this.getAggregation(aggregation.child, 'delivered')
    })
    return Promise.resolve(dateAggregationsBuckets.map((aggregation: ResponseAggregate) => {
      const clicks = this.getAggregation(aggregation.child, 'click')
      const opens = this.getAggregation(aggregation.child, 'open')
      const delivered = this.getAggregation(aggregation.child, 'delivered')
      const bounces = this.getAggregation(aggregation.child, 'hard_bounce')
      const unique_soft_bounces = this.getAggregation(aggregation.child, 'soft_bounce')
      const dropped = this.getAggregation(aggregation.child, 'dropped')
      const d = utcToZonedTime(new Date(aggregation.key), this.props.timezone)

      const partial: any = {
        timestamp: format(d, 'iii, MMM do'),
        totalClick: clicks ? clicks.doc_count : 0,
        totalDelivered: delivered ? delivered.doc_count : 0,
        totalOpen: opens ? opens.doc_count : 0,
        uniqueClick: clicks && clicks.unique ? clicks.unique.value : 0,
        uniqueDelivered: delivered && delivered.unique ? delivered.unique.value : 0,
        uniqueOpen: opens && opens.unique ? opens.unique.value : 0,
        uniqueBounces: (bounces && bounces.unique ? bounces.unique.value : 0 ) +
                       (dropped && dropped.unique ? dropped.unique.value : 0),
        uniqueSoftBounces: unique_soft_bounces && unique_soft_bounces.unique ? unique_soft_bounces.unique.value : 0,
      }

      if (this.props.timestampLabel) {
        partial.timestamp = this.props.timestampLabel(aggregation.key, aggregation.key_as_string)
      }

      return {
        ...partial,
        totalClickRate: (
          (partial.totalDelivered > 0 ?
            partial.totalClick / partial.totalDelivered : 0) * 100.0).toFixed(2),
        totalOpenRate: (
          (partial.totalDelivered > 0 ?
            partial.totalOpen / partial.totalDelivered : 0) * 100.0).toFixed(2),
        uniqueClickRate: (
          (partial.uniqueDelivered > 0 ?
            partial.uniqueClick / partial.uniqueDelivered : 0) * 100.0).toFixed(2),
        uniqueOpenRate: (
          (partial.uniqueDelivered > 0 ?
            partial.uniqueOpen / partial.uniqueDelivered : 0) * 100.0).toFixed(2),
        uniqueClickToOpenRate: (
          (partial.uniqueClick > 0 && partial.uniqueOpen > 0 ?
            partial.uniqueClick / partial.uniqueOpen : 0) * 100.0).toFixed(2),
      }
    }))
  }

  public componentDidUpdate(oldProps: EngagementProps) {
    if ( this.props.dateRange !== oldProps.dateRange  || this.props.suspectClick !== oldProps.suspectClick) {
      this.setState({ isLoadingData: true })
      this.search()
      this.tooMuchData()
    }
    if ( this.props.selectedInterval !== oldProps.selectedInterval ||
      this.props.selectedSegmentCode !== oldProps.selectedSegmentCode ) {
      this.setState({ isLoadingData: true })
      this.search()
      this.tooMuchData()
    }
  }

  public searchPayload(): any {
    const search = bodybuilder().size(0)
       .filter(FilterType.range, getMessageSendDateFilterField(this.props.dateRange, this.props.useTimezoneForAnalytics), toFilter(this.props.dateRange || DateRanges.PastMonth, this.props.timezone, this.props.useTimezoneForAnalytics))

    if (this.props.selectedSegmentCode === DEFAULT_COMMUNITY_PARTNER_CODES.NO_CODE) {
      search.notFilter(FilterType.exists, 'community_partner_code.keyword')
    } else if (this.props.selectedSegmentCode !== DEFAULT_COMMUNITY_PARTNER_CODES.ALL) {
      search.filter(FilterType.term, 'community_partner_code.keyword', this.props.selectedSegmentCode)
    }
    if (this.props.suspectClick && Utils.isRealClickSelected(this.props.suspectClick)) {
      search.addFilter(FilterType.term, 'suspect_click', 0)
    }

    return this.addAggregation(search, {
      type: AggregationType.date_histogram,
      field: getMessageSendDateFilterField(this.props.dateRange, this.props.useTimezoneForAnalytics),
      extra: appendTimezoneIfAvailable({ interval: this.props.selectedInterval || 'day' }, this.props.dateRange, this.props.timezone, this.props.useTimezoneForAnalytics),
      name: DATE_AGGREGATION,
      child: {
        type: AggregationType.terms,
        field: 'event_name.keyword',
        name: EVENT_AGGREGATION,
        unique_on: 'community_person_id',
      },
    }).build()
  }

  public tooMuchData = () => {
    const display = (this.props.results && this.props.results.length < Constants.INTERVAL_THRESHOLD)
    this.setState({displayLabel: !display})
  }

  public toggleMetric = (metricKey: string) => {
    const updatedMetrics = this.state.availableMetrics.map(metric =>
      metric.key === metricKey ? { ...metric, selected: !metric.selected } : metric
    )
    this.setState({
      availableMetrics: updatedMetrics,
      selectedMetrics: updatedMetrics.filter(m => m.selected)
    })
  }

  public toggleAxis = (metricKey: string) => {
    const updatedMetrics = this.state.availableMetrics.map(metric =>
      metric.key === metricKey ? {
        ...metric,
        axis: (metric.axis === 'left' ? 'right' : 'left')
       } : metric
    )
    this.setState({
      availableMetrics: updatedMetrics,
      selectedMetrics: updatedMetrics.filter(m => m.selected)
    })
  }

  public exportToPDF = async () => {
    if (!this.pdfExportRef.current) return

    try {
      const element = this.pdfExportRef.current

      // Temporarily add PDF export mode class to show PDF elements
      element.classList.add('pdf-export-mode')

      // Wait a brief moment for styles to apply
      await new Promise(resolve => setTimeout(resolve, 100))

      const canvas = await html2canvas(element, {
        height: element.scrollHeight,
        width: element.scrollWidth,
        useCORS: true,
        scale: 2
      })

      // Remove PDF export mode class
      element.classList.remove('pdf-export-mode')

      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('l', 'mm', 'a4') // landscape orientation

      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const imgWidth = canvas.width
      const imgHeight = canvas.height
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight)
      const imgX = (pdfWidth - imgWidth * ratio) / 2
      const imgY = 10

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio)
      pdf.save('opens-clicks-report.pdf')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error generating PDF:', error)
      // Make sure to remove the class even if there's an error
      if (this.pdfExportRef.current) {
        this.pdfExportRef.current.classList.remove('pdf-export-mode')
      }
    }
  }

  public render = () => <div className="analytics">
    {!this.props.hideMetricSelector && (
      <div className="metric-selector-compact">
        <h4>Metrics:</h4>
        <div className="metric-grid">
          {this.state.availableMetrics.map(metric => (
            <div key={metric.key} className="metric-chip">
              <label className="metric-label">
                <input
                  type="checkbox"
                  checked={metric.selected}
                  onChange={() => this.toggleMetric(metric.key)}
                />
                <span className="metric-color-dot" style={{ backgroundColor: metric.color }}></span>
                <span className="metric-text">{metric.label}</span>
              </label>
              {metric.selected && (
                <div className="axis-buttons">
                  <button
                    className={`axis-btn ${metric.axis === 'left' ? 'active' : ''}`}
                    onClick={() => metric.axis !== 'left' && this.toggleAxis(metric.key)}
                    title="Left Axis"
                  >
                    L
                  </button>
                  <button
                    className={`axis-btn ${metric.axis === 'right' ? 'active' : ''}`}
                    onClick={() => metric.axis !== 'right' && this.toggleAxis(metric.key)}
                    title="Right Axis"
                  >
                    R
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    )}
    <div className="engagement-chart">
      {this.state.isLoadingData ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Loading size="32" />
        </div>
      ) : (this.props.results && this.props.results.length > 0 ?
        <div>
          <div ref={this.pdfExportRef} className="pdf-report-container">
            <PDFReportHeader
              title="Opens & Clicks Report"
              dateRange={this.props.dateRange}
              dateRangeLabel={this.props.dateRangeLabel}
              fields={[
                { label: 'Data Grouping', value: this.props.selectedInterval },
                { label: 'Segment', value: this.props.selectedSegmentCode === 'ALL' ? 'All Segments' : this.props.selectedSegmentCode },
                { label: 'Selected Metrics', value: this.state.selectedMetrics.map(m => m.label).join(', ') },
              ]}
            />
            <div className="pdf-chart-section">
              <h3>Performance Chart</h3>
              <KendoChart.Chart transitions={false} pannable={false} zoomable={false}>
          <KendoChart.ChartLegend position="bottom" orientation="horizontal"/>
          <KendoChart.ChartTooltip
            render={({points}: KendoChart.SharedTooltipContext) => <div className="engagement-tooltip">
              <div>
                {points[0].dataItem.timestamp || ''}
              </div>
            {points.map((point) =>
              <div key={point.series.name + point.value} style={{color: point.series.color}}>
                {point.series.name}: {point.value}%
              </div>)}
            </div>}
            shared={true} />
            <KendoChart.ChartCategoryAxis>
              <KendoChart.ChartCategoryAxisItem
                categories={this.props.results.map((item) => item.timestamp)}
                axisCrossingValue={this.state.selectedMetrics.some(m => m.axis === 'right') ? [0, 999999] : [0]}/>
            </KendoChart.ChartCategoryAxis>
            <KendoChart.ChartValueAxis>
              <KendoChart.ChartValueAxisItem name="leftAxis" title={{ text: "Left Axis (%)" }} />
              {this.state.selectedMetrics.some(m => m.axis === 'right') &&
                <KendoChart.ChartValueAxisItem name="rightAxis" title={{ text: "Right Axis (%)" }} />
              }
            </KendoChart.ChartValueAxis>
            <KendoChart.ChartSeries>
              {this.state.selectedMetrics.map(metric => (
                <KendoChart.ChartSeriesItem
                  key={metric.key}
                  type="line"
                  color={metric.color}
                  field={metric.field}
                  name={metric.label}
                  data={this.props.results}
                  axis={metric.axis === 'left' ? 'leftAxis' : 'rightAxis'}>
                    {this.props.results && this.props.results.length < Constants.INTERVAL_THRESHOLD &&
                    <KendoChart.ChartSeriesLabels format="{0:N2}%" />}
                </KendoChart.ChartSeriesItem>
              ))}
            </KendoChart.ChartSeries>
              </KendoChart.Chart>
            </div>
            <div className="pdf-table-section">
              <h3>Detailed Data</h3>
              <Grid data={this.props.results} className="analytics-counts-grid pdf-table" scrollable="none">
                <GridColumn field="timestamp" title="Date"/>
                <GridColumn field="totalOpen" title="Total Opens"/>
                <GridColumn field="totalClick" title="Total Clicks"/>
                <GridColumn field="uniqueOpen" title="Unique Opens"/>
                <GridColumn field="uniqueClick" title="Unique Clicks"/>
                <GridColumn field="uniqueClickToOpenRate" title="Clicks To Open" cell={ClickToOpenCell}/>
                <GridColumn field="totalDelivered" title="Delivered"/>
                <GridColumn field="uniqueBounces" title="Bounces"/>
                <GridColumn field="uniqueSoftBounces" title="Soft Bounces"/>
              </Grid>
            </div>
          </div>
          {!this.props.hideGrid &&
          <div className="opens-clicks">
            <Button
              disabled={this.props.results.length < 1 || this.state.isLoadingData}
              onClick={() => this.xlsxExport.save()}
              style={{ marginRight: '10px' }}>
              Export xlsx
            </Button>
            <Button
              disabled={this.props.results.length < 1 || this.state.isLoadingData}
              onClick={this.exportToPDF}
              color="secondary">
              Export PDF
            </Button>
          <ExcelExport data={this.props.results}
            fileName="RasaAdminReports.xlsx"
            ref={(exporter) => {this.xlsxExport = exporter}}>
            <Grid data={this.props.results} className="analytics-counts-grid" scrollable="none">
              <GridColumn field="timestamp" title="Date"/>
              {/* <GridColumn field="totalOpenRate" title="Total Open Rate" format="{0:N2}%"/>
              <GridColumn field="totalClickRate" title="Total Click Rate" format="{0:N2}%"/>
              <GridColumn field="uniqueOpenRate" title="Unique Open Rate" format="{0:N2}%"/>
              <GridColumn field="uniqueClickRate" title="Unique Click Rate" format="{0:N2}%" /> */}
              <GridColumn field="totalOpen" title="Total Opens"/>
              <GridColumn field="totalClick" title="Total Clicks"/>
              <GridColumn field="uniqueOpen" title="Unique Opens"/>
              <GridColumn field="uniqueClick" title="Unique Clicks"/>
              <GridColumn field="uniqueClickToOpenRate" title="Clicks To Open" cell={ClickToOpenCell}/>
              <GridColumn field="totalDelivered" title="Delivered"/>
              <GridColumn field="uniqueBounces" title="Bounces"/>
              <GridColumn field="uniqueSoftBounces" title="Soft Bounces"/>
            </Grid>
          </ExcelExport>
          </div>}
        </div> :
        <div>
          <p className="no-data-tag">
            {Constants.NO_DATA_COPY}
          </p>
      </div>)}
      </div>
  </div>

}

class ClickToOpenCell extends GridCell {
  public render() {
    const d = this.props.dataItem.uniqueClickToOpenRate
    return <td>
      <span>{d}%</span>
    </td>
  }
}

export const AnalyticsOpensClicksComponent: ConnectedComponentClass<ComponentType<OpensClicksClass>, Fields> = GenericRedux.registerNewComponent(
  OpensClicksClass, 'analytics_opensclicks', {})
